<template>
    <div class="area-chart-container">
        <div ref="chartContainer" class="chart-container"></div>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import { debounce } from 'lodash';
import _ from 'lodash';

export default {
    name: 'AreaChart',
    props: {
        data: {
            type: Array,
            required: true
        },
        settings: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        fields: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            chart: null,
            resizeObserver: null
        };
    },
    computed: {
        xAxisField() {
            return this.fields.find(f => f._id === this.settings.xAxis.field);
        },
        yAxisField() {
            if (this.settings.yAxis.calculationType === 'count') return null;
            return this.fields.find(f => f._id === this.settings.yAxis.field);
        }
    },
    watch: {
        data: {
            handler() {
                this.updateChart();
            },
            deep: true
        },
        settings: {
            handler() {
                this.updateChart();
            },
            deep: true
        }
    },
    methods: {
        initChart() {
            if (this.chart) {
                this.chart.dispose();
            }

            if (!this.$refs.chartContainer || 
                !this.$refs.chartContainer.offsetWidth || 
                !this.$refs.chartContainer.offsetHeight) {
                return;
            }

            this.chart = echarts.init(this.$refs.chartContainer);
            this.updateChart();
            this.initResizeObserver();
        },
        initResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }

            this.resizeObserver = new ResizeObserver(_.debounce(() => {
                if (this.chart && this.$refs.chartContainer) {
                    requestAnimationFrame(() => {
                        this.chart.resize();
                    });
                }
            }, 100));

            if (this.$refs.chartContainer) {
                this.resizeObserver.observe(this.$refs.chartContainer);
            }
        },
        updateChart() {
            if (!this.chart || !this.data || !this.data.length) return;

            const xAxisField = this.fields.find(f => f._id === this.settings.xAxis.field);
            if (!xAxisField) return;

            const xAxisData = this.data.map(item => item[xAxisField.name]);
            const yAxisName = this.yAxisField?.name || 'value';
            const customLabel = this.$_.get(this.settings, `customization.labelCustomization.${yAxisName}`);

            const series = [{
                name: customLabel?.label || yAxisName,
                type: 'line',
                smooth: false,
                data: this.data.map(item => item.value),
                areaStyle: {},
                lineStyle: {
                    color: customLabel?.color,
                    width: 2
                },
                itemStyle: {
                    color: customLabel?.color
                }
            }];

            // Benchmark çizgilerini ekle
            const markLines = [];
            const benchmarkLines = this.settings?.customization?.benchmarkLines || [];
            if (benchmarkLines.length) {
                benchmarkLines.forEach(line => {
                    if (line.axis === 'y') {
                        markLines.push({
                            name: line.label,
                            yAxis: line.value,
                            label: {
                                show: true,
                                formatter: line.text || line.label,
                                position: 'end'
                            },
                            data: {
                                name: line.label,
                                yAxis: line.value,
                                lineStyle: {
                                    color: line.color,
                                    type: 'dashed',
                                    width: 2
                                },
                                label: {
                                    formatter: `${line.label}${line.text ? ': ' + line.text : ''}`,
                                    position: 'middle',
                                    show: true,
                                    color: line.color,
                                    fontSize: 12,
                                    backgroundColor: '#fff',
                                    padding: [4, 8]
                                }
                            }
                        });
                    }
                    if (line.axis === 'x') {
                        markLines.push({
                            name: line.label,
                            xAxis: line.value,
                            data: {
                                name: line.label,
                                xAxis: line.value,
                                lineStyle: {
                                    color: line.color,
                                    type: 'dashed',
                                    width: 2
                                },
                                label: {
                                    formatter: `${line.label}${line.text ? ': ' + line.text : ''}`,
                                    position: 'middle',
                                    show: true,
                                    color: line.color,
                                    fontSize: 12,
                                    backgroundColor: '#fff',
                                    padding: [4, 8]
                                }
                            }
                        });
                    }
                });
            }

            const option = {
                title: {
                    text: this.title,
                    left: 'center'
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'cross',
                        label: {
                            backgroundColor: '#6a7985'
                        }
                    }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    containLabel: true
                },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: xAxisData,
                    name: xAxisField.name
                },
                yAxis: {
                    type: 'value',
                    name: this.yAxisField?.name || 'Değer'
                },
                series: series.map(s => ({
                    ...s,
                    markLine: markLines.length ? {
                        symbol: ['none', 'none'],
                        silent: true,
                        data: markLines.map(x => x.data)
                    } : undefined
                }))
            };

            this.chart.setOption(option, true);
        }
    },
    mounted() {
        this.$nextTick(() => {
            requestAnimationFrame(() => {
                this.initChart();
            });
        });
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
};
</script>

<style lang="scss" scoped>
.area-chart-container {
    width: 100%;
    height: 100%;
    min-height: 200px;
    position: relative;

    .chart-container {
        width: 100%;
        height: 100%;
        min-height: 200px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }
}
</style> 